/** Client-side zip of batch outputs: the browser files are made here; server files (bulk) arrive as blobs.
 *  Bounded memory (usage QA 2026-10-07): no JSZip, no copy of the entries - see zipEntries. */
import { uniqueNames } from './save-each';
export { uniqueNames } from './save-each';
/** `source` = the original file name (optional): lets uniqueNames keep same-stem files of different formats apart. */
export interface ZipEntry { name: string; blob: Blob; source?: string; }

/** Bytes read at a time for an entry's CRC: the only part of an entry ever held in memory. */
export const ZIP_CHUNK_BYTES = 8 * 1024 * 1024;
/**
 * Largest ZIP part (usage QA J3-1/J3-3, PLAN A ruling 2): a 4.5 GB batch becomes parts of at most 1.5 GB, so no
 * part needs ZIP64 (every offset < 4 GiB) and no single download is the whole batch.
 */
export const ZIP_PART_BYTES = 1.5e9;

let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
    if (CRC_TABLE) return CRC_TABLE;
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
    return (CRC_TABLE = t);
}
function readChunk(b: Blob): Promise<Uint8Array> {
    if (typeof (b as any).arrayBuffer === 'function') return (b as any).arrayBuffer().then((x: ArrayBuffer) => new Uint8Array(x));
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(new Uint8Array(r.result as ArrayBuffer)); r.onerror = () => rej(r.error); r.readAsArrayBuffer(b); });
}
/** CRC-32 of a Blob, read ZIP_CHUNK_BYTES at a time (each chunk is dropped before the next is read). */
async function crcOf(blob: Blob): Promise<number> {
    const t = crcTable(); let c = 0xffffffff;
    for (let at = 0; at < blob.size; at += ZIP_CHUNK_BYTES) {
        const b = await readChunk(blob.slice(at, Math.min(blob.size, at + ZIP_CHUNK_BYTES)));
        for (let i = 0; i < b.length; i++) c = t[(c ^ b[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
}
function dosTime(d: Date): { time: number; date: number } {
    return { time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1), date: ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate() };
}

/**
 * The ZIP of a batch (usage QA J3-1/J3-2/J3-3): STORE (images are already compressed), written as a Blob made of
 * the entries' own Blobs plus small headers. Nothing is copied: an entry that is the user's File stays on disk,
 * and only one CRC chunk is in memory at a time. Keep the total under ZIP_PART_BYTES (splitForZip) so no
 * offset needs ZIP64. Names go through uniqueNames - the same names as "one by one" (C3); UTF-8 flag set.
 */
export async function zipEntries(entries: ZipEntry[], onProgress?: (done: number, total: number) => void): Promise<Blob> {
    const names = uniqueNames(entries.map(e => e.name), entries.map(e => e.source));
    const enc = new TextEncoder();
    const { time, date } = dosTime(new Date());
    const parts: BlobPart[] = []; const central: BlobPart[] = []; let cdSize = 0;
    let offset = 0;
    for (let i = 0; i < entries.length; i++) {
        const blob = entries[i].blob; const name = enc.encode(names[i]);
        const crc = await crcOf(blob); const size = blob.size;
        const local = new Uint8Array(30 + name.length); const lv = new DataView(local.buffer);
        lv.setUint32(0, 0x04034b50, true); lv.setUint16(4, 20, true); lv.setUint16(6, 0x0800, true); lv.setUint16(8, 0, true);
        lv.setUint16(10, time, true); lv.setUint16(12, date, true); lv.setUint32(14, crc, true); lv.setUint32(18, size, true); lv.setUint32(22, size, true);
        lv.setUint16(26, name.length, true); lv.setUint16(28, 0, true); local.set(name, 30);
        const cd = new Uint8Array(46 + name.length); const cv = new DataView(cd.buffer);
        cv.setUint32(0, 0x02014b50, true); cv.setUint16(4, 20, true); cv.setUint16(6, 20, true); cv.setUint16(8, 0x0800, true); cv.setUint16(10, 0, true);
        cv.setUint16(12, time, true); cv.setUint16(14, date, true); cv.setUint32(16, crc, true); cv.setUint32(20, size, true); cv.setUint32(24, size, true);
        cv.setUint16(28, name.length, true); cv.setUint32(42, offset, true); cd.set(name, 46);
        parts.push(local, blob); central.push(cd); cdSize += cd.length;
        offset += local.length + size;
        onProgress?.(i + 1, entries.length);
    }
    const end = new Uint8Array(22); const ev = new DataView(end.buffer);
    ev.setUint32(0, 0x06054b50, true); ev.setUint16(8, entries.length, true); ev.setUint16(10, entries.length, true);
    ev.setUint32(12, cdSize, true); ev.setUint32(16, offset, true);
    return new Blob([...parts, ...central, end], { type: 'application/zip' });
}

/** Groups entries, in order, into ZIP parts of at most `max` bytes (a lone larger entry gets a part of its own). */
export function splitForZip<T extends { blob: Blob }>(entries: T[], max: number = ZIP_PART_BYTES): T[][] {
    const out: T[][] = []; let cur: T[] = []; let size = 0;
    for (const e of entries) {
        const s = e.blob.size + 76 + 2 * ((e as any).name?.length ?? 0);   // + local and central headers
        if (cur.length && size + s > max) { out.push(cur); cur = []; size = 0; }
        cur.push(e); size += s;
    }
    if (cur.length) out.push(cur);
    return out;
}
/** "x.zip" for one part; "x-part2of3.zip" for the second of three. */
export function zipPartName(name: string, index: number, count: number): string {
    return count <= 1 ? name : name.replace(/\.zip$/i, '') + `-part${index + 1}of${count}.zip`;
}

const TOOL_ORDER = ['resize', 'crop', 'rotate', 'flip'];
const TOOL_WORD: Record<string, string> = { resize: 'resized', crop: 'cropped', rotate: 'rotated', flip: 'flipped' };

/** The ZIP is named after the operations applied to the batch (BU-9), not the tool last opened:
 *  "xconvert-resized-cropped-11-files.zip"; nothing applied = "xconvert-images-N-files.zip". */
export function zipNameFor(tools: string[], count: number): string {
    const words = TOOL_ORDER.filter(t => tools.indexOf(t) >= 0).map(t => TOOL_WORD[t]);
    return `xconvert-${words.length ? words.join('-') : 'images'}-${count}-file${count === 1 ? '' : 's'}.zip`;
}
